<template>
  <v-app>
    <v-app-bar app dense flat>
      <v-btn icon @click="drawer = !drawer">
        <v-icon>mdi-menu</v-icon>
      </v-btn>

      <div class="font-weight-bold text-subtitle-1">
        {{ businessName }}
      </div>

      <v-spacer />

      <SearchBar v-model="search" />

      <v-btn icon @click="activeTab = 'cart'">
        <v-badge :content="cartCount" :value="cartCount > 0" overlap>
          <v-icon>mdi-cart</v-icon>
        </v-badge>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" app temporary>
      <v-list dense nav>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="font-weight-bold">Departments</v-list-item-title>
            <v-list-item-subtitle>Quick jump</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider />

        <v-list-item
          v-for="d in departmentNames"
          :key="d"
          @click="selectDepartment(d)"
        >
          <v-list-item-title>{{ d }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <v-container fluid class="pa-3 pb-16">
        <Home
          v-if="activeTab === 'home'"
          :products="products"
          :search="search"
          :departmentNames="departmentNames"
          :selectedDepartment.sync="selectedDepartment"
          :cart="cart"
          @add="addToCart"
          @inc="incQty"
          @dec="decQty"
          @goBrowse="activeTab = 'browse'"
        />

        <Browse
          v-else-if="activeTab === 'browse'"
          :products="products"
          :search="search"
          :selectedDepartment.sync="selectedDepartment"
          :cart="cart"
          @add="addToCart"
          @inc="incQty"
          @dec="decQty"
        />

        <Cart
          v-else-if="activeTab === 'cart'"
          :products="products"
          :cart="cart"
          @inc="incQty"
          @dec="decQty"
          @clear="clearCart"
        />

        <Account v-else />
      </v-container>
    </v-main>

    <v-bottom-navigation app grow height="58" v-model="bottomIndex">
      <v-btn @click="activeTab = 'home'">
        <span>Home</span><v-icon>mdi-home</v-icon>
      </v-btn>
      <v-btn @click="activeTab = 'browse'">
        <span>Browse</span><v-icon>mdi-view-grid</v-icon>
      </v-btn>
      <v-btn @click="activeTab = 'cart'">
        <span>Cart</span><v-icon>mdi-cart</v-icon>
      </v-btn>
      <v-btn @click="activeTab = 'account'">
        <span>Account</span><v-icon>mdi-account</v-icon>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
import { loadRemoteProducts } from "@/modules/RemoteStore/services/productData.js ";

import SearchBar from "@/modules/RemoteStore/components/SearchBar.vue";
import Home from "@/modules/RemoteStore/view/Home.vue";
import Browse from "@/modules/RemoteStore/view/Browse.vue";
import Cart from "@/modules/RemoteStore/view/Cart.vue";
import Account from "@/modules/RemoteStore/view/Account.vue";

export default {
  name: "RemoteStoreShell",
  components: { SearchBar, Home, Browse, Cart, Account },

  data() {
    return {
      drawer: false,
      activeTab: "home",
      bottomIndex: 0,

      search: "",
      selectedDepartment: null,

      businessName: "Remote Order",
      products: [],

      cart: {}, // { [productId]: qty }
    };
  },

  async created() {
    const { business, products } = await loadRemoteProducts(this.$route.params.id);
    this.businessName = business?.name || "Remote Order";
    this.products = products || [];
  },

  computed: {
    departmentNames() {
      const set = new Set(
        (this.products || []).map((p) => (p.Department?.name ? p.Department.name : "Unassigned"))
      );
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },

    cartCount() {
      return Object.values(this.cart).reduce((a, b) => a + (Number(b) || 0), 0);
    },
  },

  watch: {
    activeTab(v) {
      const map = { home: 0, browse: 1, cart: 2, account: 3 };
      this.bottomIndex = map[v] ?? 0;
    },
  },

  methods: {
    selectDepartment(name) {
      this.selectedDepartment = name;
      this.drawer = false;
      this.activeTab = "browse";
    },

    addToCart(product) {
      const id = String(product.id);
      const next = (Number(this.cart[id]) || 0) + 1;
      this.$set(this.cart, id, next);
    },

    incQty(productId) {
      const id = String(productId);
      const next = (Number(this.cart[id]) || 0) + 1;
      this.$set(this.cart, id, next);
    },

    decQty(productId) {
      const id = String(productId);
      const next = (Number(this.cart[id]) || 0) - 1;
      if (next <= 0) this.$delete(this.cart, id);
      else this.$set(this.cart, id, next);
    },

    clearCart() {
      this.cart = {};
    },
  },
};
</script>